import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useState } from "react"
import { useAuth } from "../../../../hooks/useAuth"
import type { useCompanyApi } from "../../../../hooks/useCompanyApi"
import { useCompanyQuery } from "../../../../hooks/useCompanyQuery"
import { addVocabTerms, fetchVocab, fetchVocabStats, removeVocabTerm } from "../../../../lib/api"
import { notifyError } from "../../../../lib/notify"
import { queryKeys } from "../../../../lib/query-keys"
import { TranscriptionPanelView } from "./TranscriptionPanelView"

interface IProps {
  settings: Record<string, string>
  saving: boolean
  isAdminOnly: boolean
  /** Owner/admin (or super-admin) may edit language + cleanup; a member is read-only. */
  canManage?: boolean
  company: ReturnType<typeof useCompanyApi>
  updateSetting: (key: string, value: string) => void
  save: (keys?: string[]) => void
}

export function TranscriptionPanel({
  settings,
  saving,
  isAdminOnly,
  canManage = true,
  company,
  updateSetting,
  save,
}: IProps): React.JSX.Element {
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()

  const [vocabInput, setVocabInput] = useState("")
  const { data: vocabData } = useQuery({
    queryKey: queryKeys.vocab(orgId),
    queryFn: () => (company ? fetchVocab(company) : Promise.resolve({ terms: [] as string[] })),
    enabled: !!company || isAdminOnly,
  })
  const vocabTerms = vocabData?.terms ?? []

  const { data: vocabStatsData } = useCompanyQuery({
    key: queryKeys.vocabStats,
    queryFn: fetchVocabStats,
  })

  const { mutate: addVocab, isPending: addingVocab } = useMutation({
    mutationFn: (terms: string[]) => {
      if (!company) return Promise.resolve({ terms: [] as string[] })
      return addVocabTerms(company, terms)
    },
    onSuccess: () => {
      setVocabInput("")
      queryClient.invalidateQueries({ queryKey: queryKeys.vocab(orgId) })
    },
    onError: (err) => notifyError(err),
  })

  const { mutate: removeVocab } = useMutation({
    mutationFn: (term: string) => {
      if (!company) return Promise.resolve({ terms: [] as string[] })
      return removeVocabTerm(company, term)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.vocab(orgId) }),
    onError: (err) => notifyError(err),
  })

  const handleAddVocab = useCallback((): void => {
    const terms = vocabInput
      .split(/[,\n]+/)
      .map((t) => t.trim())
      .filter(Boolean)
    if (terms.length === 0) return
    addVocab(terms)
  }, [vocabInput, addVocab])

  return (
    <TranscriptionPanelView
      settings={settings}
      saving={saving}
      isAdminOnly={isAdminOnly}
      canManage={canManage}
      company={company}
      vocabTerms={vocabTerms}
      addingVocab={addingVocab}
      vocabInput={vocabInput}
      updateSetting={updateSetting}
      save={save}
      onVocabInputChange={setVocabInput}
      onAddVocab={handleAddVocab}
      onRemoveVocab={removeVocab}
      vocabStats={vocabStatsData?.terms}
      meetingsCounted={vocabStatsData?.meetingsCounted}
    />
  )
}
